import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Search, SlidersHorizontal, Download, Plus, ChevronDown, ChevronRight, ChevronLeft,
  Eye, Pencil, CheckCircle2, XCircle, Loader2, CircleDot, X, Trash2, AlertTriangle,
} from 'lucide-react';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function fmtDateTime(s) {
  if (!s) return { date: '—', time: '' };
  const d = new Date(s);
  if (isNaN(d)) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
}
const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const AVATAR_TONES = [
  'bg-fab-primary-lighter text-fab-primary', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700', 'bg-sky-100 text-sky-700', 'bg-rose-100 text-rose-700',
];
const toneFor = (key) => AVATAR_TONES[(key || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];

const PUSH_LABEL = {
  app: 'App', web_push: 'Web Push', app_web: 'App & Web Push', not_downloaded: 'Not Downloaded',
};

export default function AdminBuyersPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, seller_options: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({ status: '', connected_seller: '', push: '', has_orders: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [sellersModal, setSellersModal] = useState(null);
  const [detailKey, setDetailKey] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedQ.trim()) p.q = debouncedQ.trim();
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [page, pageSize, debouncedQ, filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/buyers-overview', { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: 0, seller_options: [] }))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(load, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const from = data.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, data.total);

  const openSeller = (sid) => navigate(`/dashboard/admin/sellers/${sid}/view`);

  const exportCsv = async () => {
    try {
      const p = { ...params, format: 'csv' };
      delete p.page; delete p.page_size;
      const res = await api.get('/admin/buyers-overview', { params: p, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'buyers.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const doStatus = async (row, active) => {
    try {
      await api.post(`/admin/buyers-overview/${row.buyer_key}/status`, { active });
      toast.success(active ? 'Buyer activated' : 'Buyer deactivated');
      setConfirmAction(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid="admin-buyers-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-fab-text" data-testid="buyers-title">Buyers</h2>
          <p className="text-sm text-fab-text-secondary mt-0.5">Manage and view all registered buyers across the platform.</p>
        </div>
        <div className="text-[12px] text-fab-text-muted flex items-center gap-1.5">
          <button onClick={() => navigate('/dashboard/admin')} className="hover:text-fab-primary" data-testid="crumb-dashboard">Dashboard</button>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-fab-primary font-semibold">Buyers</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl p-3 flex items-center gap-3 flex-wrap relative">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-fab-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="buyers-search"
            placeholder="Search by name, contact number, email, buyer ID…"
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-fab-primary-lighter bg-fab-base/40 focus:outline-none focus:ring-2 focus:ring-fab-primary/30"
          />
        </div>
        <div className="relative">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="buyers-filters-btn"
            className="inline-flex items-center gap-2 px-3.5 py-2.5 text-sm rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && <span className="ml-0.5 text-[10px] bg-fab-primary text-white rounded-full px-1.5 py-0.5">{activeFilterCount}</span>}
          </button>
          {showFilters && (
            <FilterPanel filters={filters} setFilters={(f) => { setFilters(f); setPage(1); }}
              sellerOptions={data.seller_options} onClose={() => setShowFilters(false)} />
          )}
        </div>
        <button onClick={exportCsv} data-testid="buyers-export-btn"
          className="inline-flex items-center gap-2 px-3.5 py-2.5 text-sm rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
          <Download className="w-4 h-4" /> Export
        </button>
        <button onClick={() => setAddOpen(true)} data-testid="buyers-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg bg-fab-primary text-white font-semibold hover:bg-fab-primary-dark transition">
          <Plus className="w-4 h-4" /> Add Buyer
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl overflow-visible">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-fab-text-muted border-b border-fab-primary-lighter">
                {['Buyer Name', 'Contact Number', 'Connected Sellers', 'Total Orders', 'No. of Purchase',
                  'Amount Spent', 'Last Active', 'App/Web Push Downloaded', 'Reviews Given', 'Edit'].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-fab-text-muted"><Loader2 className="inline w-5 h-5 animate-spin mr-2" /> Loading buyers…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-fab-text-muted" data-testid="buyers-empty">No buyers found.</td></tr>
              ) : data.items.map((r) => (
                <BuyerRow key={r.buyer_key} r={r} openSeller={openSeller}
                  onMore={() => setSellersModal(r)}
                  menuOpen={openMenu === r.buyer_key}
                  onToggleMenu={() => setOpenMenu(openMenu === r.buyer_key ? null : r.buyer_key)}
                  onView={() => { setOpenMenu(null); setDetailKey(r.buyer_key); }}
                  onEdit={() => { setOpenMenu(null); setEditRow(r); }}
                  onActivate={() => { setOpenMenu(null); setConfirmAction({ row: r, active: true }); }}
                  onDeactivate={() => { setOpenMenu(null); setConfirmAction({ row: r, active: false }); }}
                  onDelete={() => { setOpenMenu(null); setDeleteRow(r); }}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-fab-primary-lighter">
          <div className="text-[12.5px] text-fab-text-secondary" data-testid="buyers-count">
            Showing {from} to {to} of {data.total} buyers
          </div>
          <div className="flex items-center gap-2">
            <Pager page={page} totalPages={totalPages} setPage={setPage} />
            <select value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}
              data-testid="buyers-page-size"
              className="text-[12.5px] rounded-lg border border-fab-primary-lighter px-2 py-1.5 bg-white">
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>

      {sellersModal && <SellersModal row={sellersModal} openSeller={openSeller} onClose={() => setSellersModal(null)} />}
      {detailKey && <BuyerDetailModal buyerKey={detailKey} openSeller={openSeller} onClose={() => setDetailKey(null)} />}
      {editRow && <EditBuyerModal row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />}
      {confirmAction && (
        <ConfirmModal
          active={confirmAction.active} name={confirmAction.row.name}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => doStatus(confirmAction.row, confirmAction.active)}
        />
      )}
      {addOpen && <AddBuyerModal onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
      {deleteRow && <DeleteBuyerModal row={deleteRow} onClose={() => setDeleteRow(null)} onDeleted={() => { setDeleteRow(null); load(); }} />}
    </div>
  );
}

function BuyerRow({ r, openSeller, onMore, menuOpen, onToggleMenu, onView, onEdit, onActivate, onDeactivate, onDelete }) {
  const la = fmtDateTime(r.last_active_at);
  const sellers = r.connected_sellers || [];
  const shown = sellers.slice(0, 3);
  const more = sellers.length - shown.length;
  const pushDown = r.push_state !== 'not_downloaded';
  return (
    <tr className="border-b border-fab-primary-lighter/60 hover:bg-fab-base/40 align-top" data-testid={`buyer-row-${r.buyer_key}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full grid place-items-center text-[12px] font-bold shrink-0 ${toneFor(r.buyer_key)}`}>{initials(r.name)}</div>
          <div>
            <div className="font-semibold text-fab-text leading-tight">{r.name}</div>
            <div className="text-[11.5px] text-fab-text-muted">Buyer ID: {r.buyer_ref}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-fab-text">{r.phone || '—'}</div>
        <div className="text-[11.5px] text-fab-text-muted">{r.email || '—'}</div>
      </td>
      <td className="px-4 py-3 min-w-[170px]">
        {sellers.length === 0 ? <span className="text-fab-text-muted">—</span> : (
          <div className="flex flex-col gap-0.5">
            {shown.map((s) => (
              <button key={s.seller_id} onClick={() => openSeller(s.seller_id)}
                data-testid={`buyer-seller-link-${r.buyer_key}-${s.seller_id}`}
                className="text-left text-fab-primary hover:underline font-medium text-[12.5px] w-fit">{s.business_name}</button>
            ))}
            {more > 0 && (
              <button onClick={onMore} data-testid={`buyer-seller-more-${r.buyer_key}`}
                className="text-left text-fab-text-secondary hover:text-fab-primary text-[12px] w-fit">+{more} more</button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-fab-text" data-testid={`buyer-orders-${r.buyer_key}`}>{r.total_orders}</td>
      <td className="px-4 py-3 text-fab-text">{r.purchases}</td>
      <td className="px-4 py-3 text-fab-text font-medium whitespace-nowrap" data-testid={`buyer-spent-${r.buyer_key}`}>{fmtINR(r.amount_spent)}</td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-fab-text">{la.date}</div>
        <div className="text-[11.5px] text-fab-text-muted">{la.time}</div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-[12.5px]" data-testid={`buyer-push-${r.buyer_key}`}>
          <CircleDot className={`w-3.5 h-3.5 ${pushDown ? 'text-emerald-500' : 'text-rose-500'}`} />
          {PUSH_LABEL[r.push_state] || 'Not Downloaded'}
        </span>
      </td>
      <td className="px-4 py-3 text-fab-text" data-testid={`buyer-reviews-${r.buyer_key}`}>{r.reviews_given}</td>
      <td className="px-4 py-3 relative">
        <button onClick={onToggleMenu} data-testid={`buyer-edit-btn-${r.buyer_key}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
          Edit <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={onToggleMenu} />
            <div className="absolute right-4 z-40 mt-1 w-48 bg-white rounded-xl border border-fab-primary-lighter shadow-xl py-1"
              data-testid={`buyer-edit-menu-${r.buyer_key}`}>
              <MenuItem icon={Eye} onClick={onView} testid={`buyer-menu-view-${r.buyer_key}`}>View Details</MenuItem>
              <MenuItem icon={Pencil} onClick={onEdit} testid={`buyer-menu-edit-${r.buyer_key}`}>Edit Buyer Details</MenuItem>
              <MenuItem icon={CheckCircle2} onClick={onActivate} tone="emerald" testid={`buyer-menu-activate-${r.buyer_key}`}>Activate Buyer</MenuItem>
              <MenuItem icon={XCircle} onClick={onDeactivate} tone="rose" testid={`buyer-menu-deactivate-${r.buyer_key}`}>Deactivate Buyer</MenuItem>
              <div className="my-1 border-t border-fab-primary-lighter/70" />
              <MenuItem icon={Trash2} onClick={onDelete} tone="rose" testid={`buyer-menu-delete-${r.buyer_key}`}>Delete Buyer</MenuItem>
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

function MenuItem({ icon: Icon, children, onClick, tone, testid }) {
  const toneCls = tone === 'emerald' ? 'text-emerald-600' : tone === 'rose' ? 'text-rose-600' : 'text-fab-text-secondary';
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-fab-base ${toneCls}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function Pager({ page, totalPages, setPage }) {
  const nums = [];
  const push = (n) => nums.push(n);
  if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) push(i); }
  else {
    push(1);
    if (page > 3) push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) push(i);
    if (page < totalPages - 2) push('…');
    push(totalPages);
  }
  return (
    <div className="flex items-center gap-1" data-testid="buyers-pager">
      <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="buyers-prev"
        className="w-8 h-8 grid place-items-center rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:border-fab-primary text-fab-text-secondary">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {nums.map((n, i) => n === '…' ? (
        <span key={`e${i}`} className="w-8 h-8 grid place-items-center text-fab-text-muted">…</span>
      ) : (
        <button key={n} onClick={() => setPage(n)} data-testid={`buyers-page-${n}`}
          className={`w-8 h-8 grid place-items-center rounded-lg text-[12.5px] font-medium ${n === page ? 'bg-fab-primary text-white' : 'border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary'}`}>{n}</button>
      ))}
      <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="buyers-next"
        className="w-8 h-8 grid place-items-center rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:border-fab-primary text-fab-text-secondary">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function FilterPanel({ filters, setFilters, sellerOptions, onClose }) {
  const [local, setLocal] = useState(filters);
  const apply = () => { setFilters(local); onClose(); };
  const clear = () => { const c = { status: '', connected_seller: '', push: '', has_orders: '' }; setLocal(c); setFilters(c); onClose(); };
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute right-0 z-40 mt-2 w-72 bg-white rounded-xl border border-fab-primary-lighter shadow-xl p-4 space-y-3" data-testid="buyers-filter-panel">
        <Sel label="Account status" value={local.status} testid="filter-status"
          onChange={(v) => setLocal({ ...local, status: v })}
          options={[['', 'All'], ['active', 'Active'], ['inactive', 'Inactive']]} />
        <Sel label="Connected seller" value={local.connected_seller} testid="filter-seller"
          onChange={(v) => setLocal({ ...local, connected_seller: v })}
          options={[['', 'All sellers'], ...sellerOptions.map((s) => [s.seller_id, s.business_name])]} />
        <Sel label="App / Web Push" value={local.push} testid="filter-push"
          onChange={(v) => setLocal({ ...local, push: v })}
          options={[['', 'All'], ['app', 'App downloaded'], ['web_push', 'Web Push enabled'], ['app_web', 'App & Web Push'], ['not_downloaded', 'Not downloaded']]} />
        <Sel label="Orders" value={local.has_orders} testid="filter-orders"
          onChange={(v) => setLocal({ ...local, has_orders: v })}
          options={[['', 'All'], ['true', 'Buyers with orders'], ['false', 'Buyers without orders']]} />
        <div className="flex gap-2 pt-1">
          <button onClick={apply} data-testid="filter-apply" className="flex-1 py-2 rounded-lg bg-fab-primary text-white text-[12.5px] font-semibold hover:bg-fab-primary-dark">Apply</button>
          <button onClick={clear} data-testid="filter-clear" className="px-3 py-2 rounded-lg border border-fab-primary-lighter text-[12.5px] text-fab-text-secondary">Clear</button>
        </div>
      </div>
    </>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full text-[12.5px] rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function Modal({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[85vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-fab-primary-lighter sticky top-0 bg-white">
          <div className="font-display text-lg font-bold text-fab-text">{title}</div>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function SellersModal({ row, openSeller, onClose }) {
  return (
    <Modal title={`Connected Sellers · ${row.name}`} onClose={onClose} testid="buyers-sellers-modal">
      <div className="space-y-1.5">
        {row.connected_sellers.map((s) => (
          <button key={s.seller_id} onClick={() => openSeller(s.seller_id)}
            data-testid={`sellers-modal-link-${s.seller_id}`}
            className="w-full text-left px-3 py-2.5 rounded-lg border border-fab-primary-lighter hover:border-fab-primary hover:bg-fab-base transition flex items-center justify-between">
            <span className="font-medium text-fab-primary">{s.business_name}</span>
            <ChevronRight className="w-4 h-4 text-fab-text-muted" />
          </button>
        ))}
      </div>
    </Modal>
  );
}

function BuyerDetailModal({ buyerKey, openSeller, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/buyers-overview/${buyerKey}`).then(({ data }) => setD(data)).catch(() => setD(false)); }, [buyerKey]);
  return (
    <Modal title="Buyer Details" onClose={onClose} wide testid="buyers-detail-modal">
      {d === null ? <div className="py-8 text-center text-fab-text-muted"><Loader2 className="inline w-5 h-5 animate-spin" /> Loading…</div>
        : d === false ? <div className="text-rose-600">Could not load buyer.</div> : (() => {
        const b = d.buyer; const la = fmtDateTime(b.last_active_at);
        return (
          <div className="space-y-4 text-[13px]">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full grid place-items-center text-sm font-bold ${toneFor(b.buyer_key)}`}>{initials(b.name)}</div>
              <div>
                <div className="font-bold text-fab-text text-base">{b.name}</div>
                <div className="text-[12px] text-fab-text-muted">Buyer ID: {b.buyer_ref}</div>
              </div>
              <span className={`ml-auto px-3 py-1 rounded-full text-[11px] font-bold ${b.active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>{b.active ? 'ACTIVE' : 'INACTIVE'}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Info label="Contact number" value={b.phone || '—'} />
              <Info label="Email" value={b.email || '—'} />
              <Info label="City" value={b.city || '—'} />
              <Info label="App / Web Push" value={PUSH_LABEL[b.push_state]} />
              <Info label="Total orders" value={b.total_orders} />
              <Info label="No. of purchase" value={b.purchases} />
              <Info label="Amount spent" value={fmtINR(b.amount_spent)} />
              <Info label="Reviews given" value={b.reviews_given} />
              <Info label="Last active" value={`${la.date} ${la.time}`} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold mb-1.5">Connected sellers</div>
              <div className="flex flex-wrap gap-2">
                {b.connected_sellers.length === 0 ? <span className="text-fab-text-muted">—</span> : b.connected_sellers.map((s) => (
                  <button key={s.seller_id} onClick={() => { onClose(); openSeller(s.seller_id); }}
                    className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-fab-primary hover:border-fab-primary text-[12px] font-medium">{s.business_name}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold mb-1.5">Recent orders ({d.orders.length})</div>
              {d.orders.length === 0 ? <div className="text-fab-text-muted text-[12px]">No orders yet.</div> : (
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {d.orders.map((o) => (
                    <li key={o.id} className="flex items-center justify-between text-[12px] border-b border-fab-primary-lighter/60 py-1">
                      <span className="font-medium">{o.order_number}</span>
                      <span>{fmtINR(o.total_amount)}</span>
                      <span className="text-fab-text-muted">{o.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wide text-fab-text-muted font-semibold">{label}</div>
      <div className="text-fab-text mt-0.5">{value}</div>
    </div>
  );
}

function EditBuyerModal({ row, onClose, onSaved }) {
  const [name, setName] = useState(row.name || '');
  const [email, setEmail] = useState(row.email || '');
  const [city, setCity] = useState(row.city || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/buyers-overview/${row.buyer_key}`, { name, email, city });
      toast.success('Buyer updated'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Edit Buyer Details" onClose={onClose} testid="buyers-edit-modal">
      <div className="space-y-3">
        <div className="text-[12px] text-fab-text-muted">Buyer ID: {row.buyer_ref} · {row.phone}</div>
        <Fld label="Name" value={name} onChange={setName} testid="edit-name" />
        <Fld label="Email" value={email} onChange={setEmail} testid="edit-email" />
        <Fld label="City" value={city} onChange={setCity} testid="edit-city" />
        <button onClick={save} disabled={saving} data-testid="edit-save"
          className="w-full py-2.5 rounded-lg bg-fab-primary text-white font-semibold hover:bg-fab-primary-dark disabled:opacity-60">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

function AddBuyerModal({ onClose, onAdded }) {
  const [sellers, setSellers] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', seller_id: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/sellers').then(({ data }) => setSellers(data.items || [])).catch(() => setSellers([])); }, []);
  const save = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.seller_id) { toast.error('Name, phone and seller are required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/buyers-overview', form);
      toast.success('Buyer added'); onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add buyer'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add Buyer" onClose={onClose} testid="buyers-add-modal">
      <div className="space-y-3">
        <Fld label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="add-name" />
        <Fld label="Contact number *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="add-phone" />
        <Fld label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="add-email" />
        <Fld label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="add-city" />
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">Assign to seller *</span>
          <select value={form.seller_id} onChange={(e) => setForm({ ...form, seller_id: e.target.value })} data-testid="add-seller"
            className="mt-1 w-full text-[13px] rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
            <option value="">Select a seller…</option>
            {sellers.map((s) => <option key={s.id} value={s.id}>{s.business_name || s.name || s.email}</option>)}
          </select>
        </label>
        <button onClick={save} disabled={saving} data-testid="add-save"
          className="w-full py-2.5 rounded-lg bg-fab-primary text-white font-semibold hover:bg-fab-primary-dark disabled:opacity-60">
          {saving ? 'Adding…' : 'Add buyer'}
        </button>
      </div>
    </Modal>
  );
}

function Fld({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full text-[13px] rounded-lg border border-fab-primary-lighter px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-fab-primary/30" />
    </label>
  );
}

function ConfirmModal({ active, name, onCancel, onConfirm }) {
  return (
    <Modal title={active ? 'Activate Buyer' : 'Deactivate Buyer'} onClose={onCancel} testid="buyers-confirm-modal">
      <p className="text-[13px] text-fab-text-secondary">
        Are you sure you want to {active ? 'activate' : 'deactivate'} <strong className="text-fab-text">{name}</strong>?
      </p>
      <div className="flex gap-2 mt-5">
        <button onClick={onCancel} data-testid="confirm-cancel" className="flex-1 py-2.5 rounded-lg border border-fab-primary-lighter text-fab-text-secondary text-[13px]">Cancel</button>
        <button onClick={onConfirm} data-testid="confirm-ok"
          className={`flex-1 py-2.5 rounded-lg text-white font-semibold text-[13px] ${active ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}>
          {active ? 'Activate' : 'Deactivate'}
        </button>
      </div>
    </Modal>
  );
}

function DeleteBuyerModal({ row, onClose, onDeleted }) {
  const sellers = row.connected_sellers || [];
  const [sellerId, setSellerId] = useState(sellers.length === 1 ? sellers[0].seller_id : '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const canDelete = !!sellerId && text.trim().toUpperCase() === 'DELETE';
  const sellerName = (sellers.find((s) => s.seller_id === sellerId) || {}).business_name;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/buyers-overview/${row.buyer_key}`, { params: { seller_id: sellerId } });
      toast.success(`Buyer deleted permanently from ${data.seller_name || sellerName || 'seller'}.`);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Delete Buyer?" onClose={onClose} testid="buyers-delete-modal">
      <div className="space-y-3">
        <div className="flex items-start gap-2 text-[13px] text-fab-text-secondary">
          <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
          <span>This permanently deletes this buyer's chat, relationship and onboarding history <b>for the selected seller only</b>. Their orders and reviews are kept, and their data under other sellers is untouched. This cannot be undone.</span>
        </div>
        <div className="bg-fab-elevated/60 rounded-lg px-3 py-2 text-[13px]">
          <div className="font-semibold text-fab-text" data-testid="delete-buyer-name">{row.name}</div>
          <div className="text-fab-text-muted font-mono text-[12px]" data-testid="delete-buyer-phone">{row.phone || '—'}</div>
        </div>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">Seller context *</span>
          <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="delete-buyer-seller"
            className="mt-1 w-full text-[13px] rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
            <option value="">Select a connected seller…</option>
            {sellers.map((s) => <option key={s.seller_id} value={s.seller_id}>{s.business_name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[12px] text-fab-text-secondary">Type <b>DELETE</b> to confirm:</span>
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="delete-buyer-confirm-input"
            placeholder="DELETE"
            className="mt-1 w-full text-[13px] rounded-lg border border-fab-primary-lighter px-3 py-2 bg-white focus:outline-none focus:border-rose-400" />
        </label>
        <div className="flex gap-2 mt-2">
          <button onClick={onClose} disabled={busy} data-testid="delete-buyer-cancel" className="flex-1 py-2.5 rounded-lg border border-fab-primary-lighter text-fab-text-secondary text-[13px]">Cancel</button>
          <button onClick={doDelete} disabled={!canDelete || busy} data-testid="delete-buyer-confirm"
            className="flex-1 py-2.5 rounded-lg text-white font-semibold text-[13px] bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
